import { CardWithIntent, CbhIcon, Image, Text, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, Skeleton, Stack } from "@mui/material";
import { DEFAULT_QR_CODE_OPTIONS } from "@src/appV2/lib/utils/useStyledQrCodeDataUrl";
import { type TimesheetStep } from "@src/appV2/redesign/WorkerShifts/ShiftDetails/Timekeeping/hooks/useTimesheetStep";
import { useGetShiftFeedback } from "@src/appV2/ShiftFeedback/useGetShiftFeedback";
import { useQRSignatureDataUrl } from "@src/appV2/ShiftFeedback/useQrSignatureDataUrl";
import { useEffect, useRef } from "react";
import { type Control, type UseFormSetValue, useWatch } from "react-hook-form";

import { type SubmitTimesheetFormValues } from "../hooks/useTimesheetSubmission";

interface QrSignatureSectionProps {
  control: Control<SubmitTimesheetFormValues>;
  shiftFeedbackGiver: string;
  shiftId: string;
  facilityId: string;
  workerId: string;
  setValue: UseFormSetValue<SubmitTimesheetFormValues>;
  handleSubmit: () => void;
  workerFirstName: string;
  isCaliforniaTimesheet: boolean;
  setCurrentStep: (step: TimesheetStep) => void;
}

export function QrSignatureSection(props: QrSignatureSectionProps) {
  const {
    control,
    shiftFeedbackGiver,
    shiftId,
    facilityId,
    workerId,
    setValue,
    handleSubmit,
    workerFirstName,
    isCaliforniaTimesheet,
    setCurrentStep,
  } = props;

  const clockInTime = useWatch({ control, name: "clockInTime" });
  const clockOutTime = useWatch({ control, name: "clockOutTime" });
  const lunchIn = useWatch({ control, name: "breakStartTime" });
  const lunchOut = useWatch({ control, name: "breakEndTime" });
  const breakAuthorizationReason = useWatch({ control, name: "breakAuthorizationReason" });

  const otherProps = {
    shiftId,
    clockInOut: {
      start: clockInTime.toISOString(),
      end: clockOutTime.toISOString(),
    },
    lunchInOut:
      isDefined(lunchIn) && isDefined(lunchOut)
        ? {
            start: lunchIn.toISOString(),
            end: lunchOut.toISOString(),
          }
        : undefined,
    breakAuthorizationReason,
    facilityId,
    workerId,
    legacy: false,
    workerFirstName,
    isCaliforniaTimesheet,
  };

  const { data: shiftFeedbacks, isSuccess: isGetShiftFeedbackSuccess } = useGetShiftFeedback(
    {
      workerId,
      shiftId,
    },
    {
      refetchInterval: 2000,
      refetchIntervalInBackground: true,
      enabled: isDefined(workerId) && isDefined(shiftId),
    }
  );

  const { qrCodeDataUrl, isQrCodeDataUrlLoading } = useQRSignatureDataUrl(otherProps);

  const hasShiftFeedback = isGetShiftFeedbackSuccess && shiftFeedbacks?.length > 0;
  const hasSubmittedRef = useRef(false);

  useEffect(() => {
    if (hasShiftFeedback && !hasSubmittedRef.current) {
      const {
        attributes: { signedBy: facilityEmployeeName, timecard, nursingStation, nursingAssignment },
      } = shiftFeedbacks[0];

      if (isDefined(nursingStation)) {
        setValue("workerStation", nursingStation);
      }

      if (isDefined(nursingAssignment)) {
        setValue("workerNursingAssignment", nursingAssignment);
      }

      if (
        isDefined(timecard?.url) &&
        isDefined(timecard?.filename) &&
        isDefined(timecard?.fileStorageFileKey)
      ) {
        setValue("facilityEmployeeName", facilityEmployeeName);
        setValue("timecard", {
          url: timecard.url,
          filename: timecard.filename,
          fileStorageFileKey: timecard.fileStorageFileKey,
        });
      }

      if (isCaliforniaTimesheet) {
        setCurrentStep("hcp-signing");
      } else {
        handleSubmit();
        hasSubmittedRef.current = true;
      }
    }
  }, [
    hasShiftFeedback,
    shiftFeedbacks,
    setValue,
    handleSubmit,
    isCaliforniaTimesheet,
    setCurrentStep,
  ]);

  return (
    <Stack
      spacing={8}
      sx={{
        paddingX: 6,
        paddingY: 6,
        backgroundColor: (theme) => theme.palette.background.paper,
      }}
    >
      <Title bold variant="h6" component="h2">
        To get paid, ask {shiftFeedbackGiver} to scan this QR code using their camera app
      </Title>

      <Stack spacing={6}>
        {isQrCodeDataUrlLoading && (
          <Box
            sx={{
              width: "60%",
              aspectRatio: "1/1",
              alignSelf: "center",
              overflow: "hidden",
            }}
          >
            <Skeleton
              variant="rectangular"
              height={DEFAULT_QR_CODE_OPTIONS.height}
              width={DEFAULT_QR_CODE_OPTIONS.width}
              sx={{
                backgroundColor: (theme) => theme.palette.grey[500],
              }}
            />
          </Box>
        )}
        {isDefined(qrCodeDataUrl) && (
          <Image
            src={qrCodeDataUrl}
            alt="QR Code"
            style={{
              width: "60%",
              height: "60%",
              objectFit: "contain",
              alignSelf: "center",
            }}
          />
        )}
      </Stack>

      <CardWithIntent
        intent="neutral"
        sx={(theme) => ({
          padding: theme.spacing(4),
        })}
      >
        <Stack
          direction="row"
          spacing={3}
          sx={{
            alignItems: "center",
          }}
        >
          <CbhIcon type="camera-filled" size="small" />
          <Text variant="body2">Point with phone camera to scan and sign</Text>
        </Stack>
      </CardWithIntent>
    </Stack>
  );
}
